import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const CATEGORIES = ['rides', 'deliveries', 'payments', 'driver_issues', 'account', 'general'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const NewTicketModal = ({ isOpen, onClose, onTicketCreated }) => {
  const [form, setForm] = useState({
    category: 'general',
    priority: 'medium',
    subject: '',
    description: '',
    user_name: '',
    user_email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    setAttachments(prev => [...prev, ...files]?.slice(0, 5));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    setAttachments(prev => [...prev, ...files]?.slice(0, 5));
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev?.filter((_, i) => i !== idx));
  };

  const generateTicketNumber = () => {
    const num = Math.floor(Math.random() * 9000) + 1000;
    return `TKT-${num}`;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.subject?.trim()) {
      setError('Subject is required');
      return;
    }
    if (!form?.user_name?.trim()) {
      setError('User name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { error: insertError } = await supabase?.from('support_tickets')?.insert({
          ticket_number: generateTicketNumber(),
          user_name: form?.user_name?.trim(),
          user_email: form?.user_email?.trim() || null,
          category: form?.category,
          priority: form?.priority,
          subject: form?.subject?.trim(),
          description: form?.description?.trim() || null,
          ticket_status: 'open',
        });
      if (insertError) throw insertError;
      setForm({ category: 'general', priority: 'medium', subject: '', description: '', user_name: '', user_email: '' });
      setAttachments([]);
      onTicketCreated?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)', borderRadius: '16px 16px 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.2)' }}>
              <Icon name="Plus" size={18} color="#D4AF37" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Create New Ticket</h2>
              <p className="text-xs text-blue-200">Submit a support request</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} color="#fff" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* User Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">User Name *</label>
              <input
                type="text"
                value={form?.user_name}
                onChange={e => handleChange('user_name', e?.target?.value)}
                placeholder="Full name"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#D4AF37' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={form?.user_email}
                onChange={e => handleChange('user_email', e?.target?.value)}
                placeholder="user@email.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#D4AF37' }}
              />
            </div>
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Category *</label>
              <select
                value={form?.category}
                onChange={e => handleChange('category', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                style={{ '--tw-ring-color': '#D4AF37' }}
              >
                {CATEGORIES?.map(cat => (
                  <option key={cat} value={cat}>{cat?.replace('_', ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Priority *</label>
              <select
                value={form?.priority}
                onChange={e => handleChange('priority', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                style={{ '--tw-ring-color': '#D4AF37' }}
              >
                {PRIORITIES?.map(p => (
                  <option key={p} value={p}>{p?.charAt(0)?.toUpperCase() + p?.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Subject *</label>
            <input
              type="text"
              value={form?.subject}
              onChange={e => handleChange('subject', e?.target?.value)}
              placeholder="Brief description of the issue"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': '#D4AF37' }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
            <textarea
              value={form?.description}
              onChange={e => handleChange('description', e?.target?.value)}
              placeholder="Provide detailed information about the issue..."
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
              style={{ '--tw-ring-color': '#D4AF37' }}
            />
          </div>

          {/* File Attachment Drop Zone */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Attachments</label>
            <div
              onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
              className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-200"
              style={{ borderColor: dragOver ? '#D4AF37' : '#d1d5db', background: dragOver ? '#fffbeb' : '#f9fafb' }}
            >
              <Icon name="Upload" size={20} className="mx-auto mb-1 text-gray-400" />
              <p className="text-xs text-gray-500">Drag & drop files or <span style={{ color: '#D4AF37' }} className="font-semibold">browse</span></p>
              <p className="text-xs text-gray-400 mt-0.5">Max 5 files · PNG, JPG, PDF</p>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleFileSelect} />
            </div>
            {attachments?.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments?.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                    <Icon name="File" size={13} className="text-gray-400" />
                    <span className="text-xs text-gray-700 flex-1 truncate">{file?.name}</span>
                    <button type="button" onClick={() => removeAttachment(idx)} className="p-0.5 hover:text-red-500 transition-colors">
                      <Icon name="X" size={12} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <Icon name="AlertCircle" size={14} className="text-red-500" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #0f2044, #1a3a6e)', color: '#D4AF37' }}
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Icon name="Send" size={15} />
                  Create Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTicketModal;
